import React, { useState } from 'react';
import {
  Button,
} from '@patternfly/react-core';
import { EyeIcon, EyeSlashIcon } from '@patternfly/react-icons'
import {
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  TableVariant,
} from '@patternfly/react-table';
import {
  ResourceItem,
  ResourceItemVariants,
  ResourceItemType
} from '@app/components/ResourceItem';
import { Link } from 'react-router-dom';
import { KeyvaultKey } from '../model/repository';
import useSecretDetails from './useSecretDetails';
import Timestamp from 'react-timestamp';
import { SecretDetailEvent } from './../../../../../shared/secretmanager/secretsexplorer/types';

const SecretDetails: React.FunctionComponent<{
  root: string;
  namespaceName: string;
  applicationName: string;
  containerName: string;
  itemName: string;
  item: any;
  isFile: boolean;
  usedBy: KeyvaultKey.Reference[];
  history: SecretDetailEvent[];
  toggleValueVisibility: () => void;
  isValueHidden: boolean;
  canReveal: boolean;
  secretValue: string | undefined;
  loading: boolean;
  error: Error | null
}> = ({
  item,
  root,
  namespaceName,
  applicationName,
  containerName,
  itemName,
  isFile,
  history,
  usedBy,
  canReveal,
  loading,
  secretValue,
  toggleValueVisibility,
  isValueHidden,
  error
}) => {
    const [showErrorBanner, setShowErrorBanner] = useState(false);
    const [errorBannerMessage, setErrorBannerMessage] = useState("");

    if (error && !showErrorBanner) {
      setErrorBannerMessage(error.message);
      setShowErrorBanner(true);
    }

    const secretProvider = `${item?.secret_provider_key_ref.secret_provider_name}/${item?.secret_provider_key_ref.secret_provider_key}`;
    const keyvault = `${item?.keyvault_key_ref.keyvault_name}/${item?.keyvault_key_ref.keyvault_key}`;

    const renderHistoryTable = () => (
      <Table variant={TableVariant.compact}>
        <Thead>
          <Tr>
            <Th>Date</Th>
            <Th>Change request</Th>
            <Th>Description</Th>
            <Th>Author</Th>
          </Tr>
        </Thead>
        <Tbody>
          {history.map((entry, index) => (
            <Tr key={index}>
              <Td>
                <Timestamp date={entry.date} options={{ includeDay: false }} />
              </Td>
              <Td>
                {entry.change_request ?
                <ResourceItem type={ResourceItemType.ChangeRequest}>
                  <Link to={`/secretmanager/changerequests/view/${entry.change_request}`}>
                    {entry.change_request}
                  </Link>
                </ResourceItem> : "-" }
              </Td>
              <Td>{entry.description}</Td>
              <Td>{entry.author}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );

    const renderUsedByTable = () => {

      return (
        <Table variant={TableVariant.compact}>
          <Thead>
            <Tr>
              <Th>Environment</Th>
              <Th>Application</Th>
              <Th>Container</Th>
              <Th>Variable or File</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usedBy.length === 0 ?
              <Tr><Td colSpan={8}>
                <div className="dx-bullseye">
                  <div className="dx-empty-state dx-empty-state--sm">
                    <h2 className="dx-empty-state__title">No cross-references found</h2>
                    <div className="dx-empty-state__body">
                      This keyvault secret is not referenced by any other application.
                    </div>
                  </div>
                </div>
              </Td></Tr>

              :
              usedBy.map((usage, index) => (
                <Tr key={index}>
                  <Td>{usage.namespace}</Td>
                  <Td>{usage.application.name}</Td>
                  <Td>{usage.container.name}</Td>
                  <Td>{usage.secret.name}</Td>
                </Tr>
              ))}


          </Tbody>
        </Table>
      );
    };

    return (
      <>
        <section className="dx-page-section">
          <nav className="dx-breadcrumb">
            <span className="dx-breadcrumb__item"><Link className="dx-breadcrumb__link" to={`${root}`}>Secret Manager</Link></span>
            <span className="dx-breadcrumb__item"><Link className="dx-breadcrumb__link" to={`${root}/${namespaceName}`}>{namespaceName}</Link></span>
            <span className="dx-breadcrumb__item"><Link className="dx-breadcrumb__link" to={`${root}/${namespaceName}/${applicationName}`}>{applicationName}</Link></span>
            <span className="dx-breadcrumb__item"><Link className="dx-breadcrumb__link" to={`${root}/${namespaceName}/${applicationName}/${containerName}`}>{containerName}</Link></span>
            <span className="dx-breadcrumb__item">{isFile ? 'File' : 'Secret'} Details</span>
          </nav>
          <ResourceItem variant={ResourceItemVariants.h1} type={ResourceItemType.Secret}>{decodeURIComponent(itemName)}</ResourceItem>
        </section>
        <hr className="dx-divider" />
        {showErrorBanner &&
          <div className="dx-banner dx-banner--danger">
            <div className="dx-flex dx-flex--gap-sm">
              <div>
                {errorBannerMessage}
              </div>
            </div>
          </div>
        }

        <>
          <section className="dx-page-section">
            <h3 className="dx-title" style={{ marginBottom: '10px' }}>Details</h3>
            <dl className="dx-description-list">
              <div className="dx-dl-group">
                <dt className="dx-dl-term">Secret provider</dt>
                <dd className="dx-dl-desc">{secretProvider}</dd>
              </div>
              <div className="dx-dl-group">
                <dt className="dx-dl-term">Keyvault</dt>
                <dd className="dx-dl-desc">{keyvault}</dd>
              </div>
              <div className="dx-dl-group">
                <dt className="dx-dl-term">Value</dt>
                <dd className="dx-dl-desc">
                  {isValueHidden ? '••••••••••' : secretValue}
                  <Button isDisabled={!canReveal} isLoading={loading} variant="plain" aria-label={isValueHidden ? 'Reveal value' : 'Hide value'} isInline onClick={toggleValueVisibility} style={{ padding: 0, paddingLeft: 6 }}>
                    {isValueHidden ? <EyeIcon /> : <EyeSlashIcon />}
                  </Button>
                </dd>
              </div>
            </dl>


          </section>
          <hr className="dx-divider" />
          <section className="dx-page-section">
            <h3 className="dx-title" style={{ marginBottom: '10px' }}>History</h3>
            {renderHistoryTable()}
          </section>
          <hr className="dx-divider" />
          <section className="dx-page-section">
            <h3 className="dx-title" style={{ marginBottom: '10px' }}>Also used by</h3>
            {renderUsedByTable()}
          </section>
        </>

      </>
    );
  };

export default SecretDetails;
